import React, { useState } from 'react';
import { fetchApi } from '../services/api';
import { Plus, Clock, AlarmClock, Link as LinkIcon, FileText, Type } from 'lucide-react';

export default function AddEventForm({ channelId, selectedGroupId, onSelectGroup, onEventAdded }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [url, setUrl] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [alarms, setAlarms] = useState([]);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const alarmOptions = [
    { label: '5 dk önce', value: '-PT5M' },
    { label: '15 dk önce', value: '-PT15M' },
    { label: '1 saat önce', value: '-PT1H' },
    { label: '1 gün önce', value: '-P1D' },
  ];

  const handleStartTimeChange = (e) => {
    const value = e.target.value;
    setStartTime(value);

    const startDate = new Date(value);
    if (!isNaN(startDate.getTime())) {
      const endDate = new Date(startDate.getTime() + 60 * 60 * 1000);
      const pad = (num) => String(num).padStart(2, '0');
      const localFormatted = `${endDate.getFullYear()}-${pad(endDate.getMonth() + 1)}-${pad(endDate.getDate())}T${pad(endDate.getHours())}:${pad(endDate.getMinutes())}`;
      setEndTime(localFormatted);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    let groupId = selectedGroupId;

    if (!groupId) {
      const now = new Date();
      const formattedDate = now.toLocaleDateString('tr-TR', {
        day: 'numeric', month: 'long', year: 'numeric',
      });

      try {
        const groupData = await fetchApi('/groups', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: formattedDate, channelId }),
        });
        groupId = groupData.id;
        onSelectGroup?.(groupId);
      } catch (err) {
        setMessage(err.message || 'Grup oluşturulurken hata oluştu.');
        setSubmitting(false);
        return;
      }
    }

    try {
      await fetchApi('/events', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          channelId, groupId, title, description, url, alarms,
          start_time: startTime, end_time: endTime,
        }),
      });
      setMessage('Etkinlik başarıyla eklendi.');
      setTitle('');
      setDescription('');
      setUrl('');
      setStartTime('');
      setEndTime('');
      setAlarms([]);
      onEventAdded?.();
    } catch (err) {
      setMessage(err.message || 'Sunucu hatası.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '0.7rem 0.85rem',
    border: '1.5px solid var(--border-color)',
    borderRadius: 'var(--radius-sm)',
    fontSize: '1rem',
    background: 'var(--bg-secondary)',
    color: 'var(--text-primary)',
    outline: 'none',
    transition: 'border-color var(--transition-fast), box-shadow var(--transition-fast)',
  };

  const handleFocus = (e) => {
    e.target.style.borderColor = 'var(--primary-400)';
    e.target.style.boxShadow = '0 0 0 3px rgba(99, 102, 241, 0.1)';
  };

  const handleBlur = (e) => {
    e.target.style.borderColor = 'var(--border-color)';
    e.target.style.boxShadow = 'none';
  };

  return (
    <div style={{ padding: '1.25rem' }}>
      <form onSubmit={handleSubmit}>
        <h3 style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          color: 'var(--primary-400)',
          marginBottom: '1.25rem',
          fontSize: '1.125rem',
          fontWeight: '600',
        }}>
          <Plus size={20} />
          Yeni Etkinlik Ekle
        </h3>

        {/* Title */}
        <div style={{ marginBottom: '0.85rem' }}>
          <label style={{
            display: 'flex', alignItems: 'center', gap: '5px',
            fontWeight: '500', color: 'var(--text-secondary)',
            marginBottom: '0.35rem', fontSize: '0.875rem',
          }}>
            <Type size={13} /> Başlık
          </label>
          <input
            type="text"
            placeholder="Etkinlik başlığı"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            style={inputStyle}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
        </div>

        {/* Description */}
        <div style={{ marginBottom: '0.85rem' }}>
          <label style={{
            display: 'flex', alignItems: 'center', gap: '5px',
            fontWeight: '500', color: 'var(--text-secondary)',
            marginBottom: '0.35rem', fontSize: '0.875rem',
          }}>
            <FileText size={13} /> Açıklama
          </label>
          <textarea
            placeholder="Etkinlik açıklaması (isteğe bağlı)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            style={{ ...inputStyle, resize: 'vertical', minHeight: '70px' }}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
        </div>

        {/* URL */}
        <div style={{ marginBottom: '0.85rem' }}>
          <label style={{
            display: 'flex', alignItems: 'center', gap: '5px',
            fontWeight: '500', color: 'var(--text-secondary)',
            marginBottom: '0.35rem', fontSize: '0.875rem',
          }}>
            <LinkIcon size={13} /> URL (isteğe bağlı)
          </label>
          <input
            type="url"
            placeholder="https://..."
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            style={inputStyle}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
        </div>

        {/* Date/Time Row */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.85rem' }}>
          <div>
            <label style={{
              display: 'flex', alignItems: 'center', gap: '5px',
              fontWeight: '500', color: 'var(--text-secondary)',
              marginBottom: '0.35rem', fontSize: '0.875rem',
            }}>
              <Clock size={13} /> Başlangıç
            </label>
            <input
              type="datetime-local"
              value={startTime}
              onChange={handleStartTimeChange}
              required
              style={inputStyle}
              onFocus={handleFocus}
              onBlur={handleBlur}
            />
          </div>
          <div>
            <label style={{
              display: 'flex', alignItems: 'center', gap: '5px',
              fontWeight: '500', color: 'var(--text-secondary)',
              marginBottom: '0.35rem', fontSize: '0.875rem',
            }}>
              <Clock size={13} /> Bitiş
            </label>
            <input
              type="datetime-local"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              required
              style={inputStyle}
              onFocus={handleFocus}
              onBlur={handleBlur}
            />
          </div>
        </div>

        {/* Alarms */}
        <div style={{ marginBottom: '1.25rem' }}>
          <label style={{
            display: 'flex', alignItems: 'center', gap: '5px',
            fontWeight: '500', color: 'var(--text-secondary)',
            marginBottom: '0.5rem', fontSize: '0.875rem',
          }}>
            <AlarmClock size={13} /> Hatırlatma
          </label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {alarmOptions.map(option => {
              const isChecked = alarms.includes(option.value);
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    if (isChecked) {
                      setAlarms(prev => prev.filter(a => a !== option.value));
                    } else {
                      setAlarms(prev => [...prev, option.value]);
                    }
                  }}
                  style={{
                    padding: '0.4rem 0.75rem',
                    borderRadius: 'var(--radius-full)',
                    border: `1.5px solid ${isChecked ? 'var(--primary-500)' : 'var(--border-color)'}`,
                    background: isChecked ? 'var(--primary-50)' : 'transparent',
                    color: isChecked ? 'var(--primary-400)' : 'var(--text-secondary)',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all var(--transition-fast)',
                  }}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={submitting}
          style={{
            width: '100%',
            padding: '0.75rem',
            background: 'var(--primary-600)',
            color: '#fff',
            fontWeight: '600',
            fontSize: '1rem',
            border: 'none',
            borderRadius: 'var(--radius-sm)',
            cursor: submitting ? 'wait' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            transition: 'background var(--transition-fast)',
            opacity: submitting ? 0.7 : 1,
          }}
          onMouseEnter={(e) => {
            if (!submitting) e.currentTarget.style.background = 'var(--primary-700)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'var(--primary-600)';
          }}
        >
          <Plus size={18} />
          {submitting ? 'Ekleniyor...' : 'Etkinlik Ekle'}
        </button>

        {message && (
          <p style={{
            textAlign: 'center',
            marginTop: '0.85rem',
            padding: '0.6rem',
            borderRadius: 'var(--radius-sm)',
            fontSize: '0.875rem',
            fontWeight: '500',
            background: message.includes('başarıyla') ? 'var(--accent-50)' : 'var(--danger-50)',
            color: message.includes('başarıyla') ? 'var(--accent-600)' : 'var(--danger-600)',
          }}>
            {message}
          </p>
        )}
      </form>
    </div>
  );
}
